import React, { useState } from 'react';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useHouse, useExportHouseDetail } from '../hooks/useHouses.ts';
import { Home, Users, CreditCard, Calendar, FileText, Download, Info } from 'lucide-react';
import Table from '../components/Table.tsx';

export default function MyHousePage() {
  const { data: profile, isLoading: isProfileLoading } = useGetProfile();
  const currentGroup = profile?.data?.current_group;
  const isHouseManagement = Number(currentGroup?.is_house_management) === 1;
  const houseLabel = isHouseManagement ? (currentGroup?.house_label || 'House') : 'House';
  const tenantLabel = isHouseManagement ? (currentGroup?.tenant_label || 'Member') : 'Member';

  const houseId = profile?.data?.member?.house_id;

  const { data: detailData, isLoading: isDetailLoading } = useHouse(houseId);
  const detail = detailData?.data || null;

  const exportDetailMutation = useExportHouseDetail();
  const [showAllMilestones, setShowAllMilestones] = useState(false);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (!houseId) return;
    const blob = await exportDetailMutation.mutateAsync(houseId);
    downloadBlob(blob, `${houseLabel.toLowerCase()}_${(detail?.house?.name || 'detail').replace(/\s+/g, '_')}_ledger.csv`);
  };

  if (isProfileLoading || isDetailLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center space-y-2">
          <div className="mx-auto w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          <p className="text-sm text-gray-500">Loading {houseLabel.toLowerCase()} details...</p>
        </div>
      </div>
    );
  }

  if (!houseId || !detail) {
    return (
      <div className="p-6 max-w-xl mx-auto text-center mt-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 p-8 space-y-4">
        <div className="p-4 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 w-16 h-16 mx-auto flex items-center justify-center">
          <Home size={32} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">No {houseLabel} Assigned</h1>
        <p className="text-gray-500">
          You are currently not assigned to any {houseLabel.toLowerCase()}. Please contact your group administrator to link your account.
        </p>
      </div>
    );
  }

  const monthPaid = Number(detail.this_month_paid || 0);
  const monthExpected = Number(detail.this_month_expected || 0);
  const monthBalance = Number(detail.this_month_balance || 0);
  const monthOver = monthExpected > 0 ? Math.max(0, monthPaid - monthExpected) : 0;
  const settled = monthExpected > 0 && monthBalance <= 0;

  const membersColumns = [
    {
      label: 'Name',
      field: 'name',
      render: (m) => (
        <div>
          <span className="font-semibold text-gray-900 dark:text-white block">
            {m.name} {m.id === profile?.data?.member?.id && <span className="text-xs text-blue-600 font-normal bg-blue-50 px-2 py-0.5 rounded-full ml-1">You</span>}
          </span>
          {m.member_no && <span className="text-xs text-gray-500 block">No. {m.member_no}</span>}
        </div>
      ),
    },
    {
      label: 'Phone Number',
      field: 'phone_number',
      render: (m) => <span>{m.phone_number || '-'}</span>,
    },
    {
      label: 'Status',
      field: 'is_active',
      render: (m) => (
        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${m.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {m.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const statementColumns = [
    {
      label: 'Payment Date',
      field: 'contribution_date',
      render: (c) => (
        <div className="flex items-center space-x-2">
          <Calendar size={14} className="text-gray-400" />
          <span>{c.contribution_date || '-'}</span>
        </div>
      ),
    },
    {
      label: `${tenantLabel} Name`,
      field: 'member_name',
      render: (c) => (
        <div>
          <span className="font-semibold text-gray-800 dark:text-gray-200">{c.member_name}</span>
          {c.member_no && <span className="text-xs text-gray-500 block">No. {c.member_no}</span>}
        </div>
      ),
    },
    {
      label: 'Notes / Reference',
      field: 'notes',
      render: (c) => <span className="text-gray-500 italic text-sm">{c.notes || '-'}</span>,
    },
    {
      label: 'Payment Method',
      field: 'payment_method',
      render: (c) => (
        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 uppercase">
          {c.payment_method || '-'}
        </span>
      ),
    },
    {
      label: 'Amount Paid',
      field: 'amount',
      render: (c) => (
        <span className="font-bold text-emerald-600">
          + KSh {Number(c.amount || 0).toLocaleString()}
        </span>
      ),
    },
  ];

  const house = detail?.house || detail?.subgroup || {};
  const members = Array.isArray(detail?.members) ? detail.members : [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
            <Home size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">My {houseLabel}: {house.name || 'House'}</h1>
            <p className="text-sm text-gray-500 mt-1">{house.description || `Overview of co-occupants and consolidated payments for ${house.name || 'this house'}.`}</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exportDetailMutation.isPending}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <Download size={16} />
          <span>{exportDetailMutation.isPending ? 'Exporting...' : 'Export Statement'}</span>
        </button>
      </div>

      {/* Stats summary */}
      <div className={`grid grid-cols-1 ${detail.has_target ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">Co-Occupants</span>
              <Info size={14} className="text-gray-400 cursor-help" title={`Total number of members living in this ${houseLabel.toLowerCase()}.`} />
            </div>
            <span className="text-3xl font-extrabold text-gray-900 dark:text-white mt-1 block">{members.length} {tenantLabel}s</span>
          </div>
          <div className="p-4 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400">
            <Users size={28} />
          </div>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-1">
              <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">All-Time Collected (Lifetime)</span>
              <Info size={14} className="text-gray-400 cursor-help" title={`Total lifetime payments recorded for this ${houseLabel.toLowerCase()}.`} />
            </div>
            <span className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1 block">
              KSh {Number(detail.total_contributions || 0).toLocaleString()}
            </span>
          </div>
          <div className="p-4 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
            <CreditCard size={28} />
          </div>
        </div>

        {detail.has_target && (() => {
          const settled = detail.this_month_status === 'Settled';
          const captionCls = settled ? 'text-emerald-600' : 'text-amber-600';
          return (
            <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-gray-500 font-medium uppercase tracking-wider block">This Month</span>
                  <Info size={14} className="text-gray-400 cursor-help" title="Amount collected towards this month's specific target." />
                </div>
                <span className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1 block">
                  KSh {Math.min(monthPaid, monthExpected).toLocaleString()} <span className="text-base font-medium text-gray-500">/ {monthExpected.toLocaleString()}</span>
                </span>
                <span className={`text-sm font-semibold ${captionCls} block mt-1`}>
                  {detail.this_month_status}
                </span>
              </div>
              <div className="p-4 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                <Calendar size={28} />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Progress card with debt / paid / overpay (mirrors admin view) */}
      {detail.has_target && (() => {
        const paidThru = Number(detail.progress_paid_to_date || 0);
        const expectedThru = Number(detail.progress_expected_to_date || 0);
        const debt = Number(detail.progress_debt || 0);
        const overpay = Number(detail.progress_overpay || 0);
        const totalPaid = Number(detail.target_paid || 0);
        const scale = Math.max(expectedThru, totalPaid, 1);
        const paidPct = (paidThru / scale) * 100;
        const debtPct = (debt / scale) * 100;
        const overpayPct = (overpay / scale) * 100;
        const statusLabel = debt > 0
          ? `Behind by KSh ${debt.toLocaleString()}`
          : overpay > 0
            ? `Overpaid by KSh ${overpay.toLocaleString()}`
            : expectedThru > 0
              ? 'Up to date'
              : 'No monthly target';
        const statusCls = debt > 0
          ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/20 dark:text-rose-400'
          : overpay > 0
            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300'
            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400';

        const milestones = detail.monthly_milestones || [];
        const isRelevant = (m) => {
          if (m.is_current) return true;
          if (detail.current_month_label && m.label === detail.current_month_label) return true;
          if (m.status === 'missed' || m.status === 'short' || m.status === 'due') return true;
          if (m.status === 'overpaid' || Number(m.overflow || 0) > 0) return true;
          return false;
        };
        let visible = showAllMilestones ? milestones : milestones.filter(isRelevant);
        if (visible.length === 0 && milestones.length > 0) {
          const curIdx = milestones.findIndex(m =>
            m.is_current || (detail.current_month_label && m.label === detail.current_month_label)
          );
          visible = curIdx >= 0 ? [milestones[curIdx]] : [milestones[0]];
        }
        const visibleKeys = new Set(visible.map(m => m.key));
        const someHidden = milestones.length > visible.length;

        return (
          <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Progress{detail.current_month_label ? ` · through ${detail.current_month_label}` : ''}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Target period: {detail.target_from_date ? new Date(detail.target_from_date).toLocaleDateString() : '-'} – {detail.target_to_date ? new Date(detail.target_to_date).toLocaleDateString() : '—'} &middot; KSh {monthExpected.toLocaleString()} expected each month
                </p>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusCls}`}>{statusLabel}</span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300">
                <span>Paid: <strong className="text-emerald-700 dark:text-emerald-400">KSh {paidThru.toLocaleString()}</strong></span>
                <span>Required to date: <strong className="text-gray-900 dark:text-white">KSh {expectedThru.toLocaleString()}</strong></span>
              </div>

              <div className="w-full flex bg-gray-100 dark:bg-gray-700 h-3 rounded-full overflow-hidden">
                {paidPct > 0 && (
                  <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${paidPct}%` }} title={`Paid: KSh ${paidThru.toLocaleString()}`} />
                )}
                {debtPct > 0 && (
                  <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${debtPct}%` }} title={`Debt: KSh ${debt.toLocaleString()}`} />
                )}
                {overpayPct > 0 && (
                  <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${overpayPct}%` }} title={`Overpaid: KSh ${overpay.toLocaleString()}`} />
                )}
              </div>

              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Paid KSh {paidThru.toLocaleString()}</span>
                {debt > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> Debt KSh {debt.toLocaleString()}</span>}
                {overpay > 0 && <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Pre-pay KSh {overpay.toLocaleString()}</span>}
              </div>

              <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-1">
                Paid in this calendar month: KSh {monthPaid.toLocaleString()} &middot; Total in period: KSh {totalPaid.toLocaleString()} of {Number(detail.target_amount || 0).toLocaleString()}
              </p>
            </div>

            {milestones.length > 0 && (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Monthly Milestones</h4>
                    <p className="text-xs text-gray-500">
                      KSh {Number(detail.expected_per_month || 0).toLocaleString()} expected each month &middot; {detail.months_completed}/{detail.total_months} months covered
                    </p>
                  </div>
                  {someHidden && (
                    <button
                      onClick={() => setShowAllMilestones(v => !v)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
                    >
                      {showAllMilestones ? 'Show relevant only' : `Show all ${milestones.length}`}
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {visible.map((m) => {
                    const cls = m.status === 'paid'
                      ? 'bg-emerald-500 border-emerald-600 text-white'
                      : m.status === 'overpaid'
                      ? 'bg-purple-500 border-purple-600 text-white'
                      : m.status === 'partial'
                      ? 'bg-blue-500 border-blue-600 text-white'
                      : m.status === 'short'
                      ? 'bg-amber-400 border-amber-500 text-amber-950'
                      : (m.status === 'missed' || m.status === 'due')
                      ? 'bg-rose-500 border-rose-600 text-white'
                      : 'bg-gray-100 border-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
                    const ring = m.is_current ? 'ring-2 ring-blue-500 ring-offset-1' : '';
                    const title = `${m.label}: KSh ${Number(m.paid).toLocaleString()} / ${Number(m.expected).toLocaleString()}${m.overflow ? ` (+${Number(m.overflow).toLocaleString()} overflow)` : ''} — ${m.status}`;
                    return (
                      <div
                        key={m.key}
                        title={title}
                        className={`px-2 py-1 rounded-md text-[10px] font-semibold border ${cls} ${ring} min-w-[64px] text-center`}
                      >
                        <div>{m.label}</div>
                        {m.overflow > 0 && <div className="text-[9px] opacity-90">+{Number(m.overflow).toLocaleString()}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tables section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Occupants */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Users size={20} className="text-gray-500" />
            <span>Co-Occupants</span>
          </h2>
          <Table
            columns={membersColumns}
            data={detail.members}
            loading={isDetailLoading}
          />
        </div>

        {/* Consolidated statements */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <FileText size={20} className="text-gray-500" />
            <span>Consolidated Payment History</span>
          </h2>
          <Table
            columns={statementColumns}
            data={detail.contributions}
            loading={isDetailLoading}
          />
        </div>
      </div>
    </div>
  );
}
